import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
  ChevronLeft,
  Save,
  User,
  Calendar,
  MapPin,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2,
  Upload,
  X,
  Phone,
  Shield,
  Plane,
  Activity,
  UserPlus,
  CreditCard,
  AlertTriangle,
  History,
} from "lucide-react";

const API_BASE = "https://us-central1-xfinance-12e10.cloudfunctions.net";

const EditPolicy = () => {
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const { policyId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [policy, setPolicy] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (user && policyId) {
      loadPolicy();
    }
  }, [user, policyId]);

  const loadPolicy = async () => {
    if (!user || !policyId) return;

    setLoading(true);
    try {
      // Get policy from Firestore
      const policyDoc = await getDoc(doc(db, "policies", policyId));
      if (!policyDoc.exists()) {
        setMessage({ text: "Póliza no encontrada", type: "error" });
        setTimeout(() => navigate("/polizas"), 2000);
        return;
      }

      const policyData = { id: policyDoc.id, ...policyDoc.data() };

      // Check if user owns this policy or is admin/asesor
      let userData = {};
      if (policyData.userId !== user.uid) {
        const userDoc = await getDoc(doc(db, "Users", user.uid));
        userData = userDoc.exists() ? userDoc.data() : {};
        if (userData.type !== "admin" && userData.type !== "asesor") {
          setMessage({ text: "No autorizado", type: "error" });
          setTimeout(() => navigate("/polizas"), 2000);
          return;
        }
      } else {
        const userDoc = await getDoc(doc(db, "Users", user.uid));
        userData = userDoc.exists() ? userDoc.data() : {};
      }

      setPolicy(policyData);

      // Initialize form data
      const initialFormData = {
        nombreCompleto: policyData.nombreCompleto || "",
        fechaNacimiento: policyData.fechaNacimiento || "",
        fechaSalida: policyData.fechaSalida || "",
        fechaRegreso: policyData.fechaRegreso || "",
        lugaresVisita: policyData.lugaresVisita || "",
        actividadExtrema: policyData.actividadExtrema || "",
        tieneActividadExtrema: policyData.tieneActividadExtrema || false,
        beneficiario: policyData.beneficiario || "",
        contactoEmergencia: policyData.contactoEmergencia || "",
        celularEmergencia: policyData.celularEmergencia || "",
        requiereFactura: policyData.requiereFactura || false,
        csf: policyData.csf || "",
        notas: policyData.notas || "",
        reason: "",
      };

      setFormData(initialFormData);

      // Load history if admin/asesor
      if (userData.type === "admin" || userData.type === "asesor" || policyData.userId === user.uid) {
        loadHistory();
      }
    } catch (error) {
      console.error("Error loading policy:", error);
      setMessage({ text: "Error al cargar la póliza", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/policiesAPI?userId=${user.uid}&policyId=${policyId}&action=history`
      );
      const data = await response.json();
      if (data.success) {
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error("Error loading history:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!showWarning && policy?.estado === "aprobada" && policy?.userId === user.uid) {
      setShowWarning(true);
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      // Prepare update data (exclude reason from the update)
      const { reason, ...updateData } = formData;

      const response = await fetch(
        `${API_BASE}/policiesAPI?userId=${user.uid}&policyId=${policyId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...updateData, reason }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setMessage({
          text: data.statusChanged
            ? "Póliza actualizada. La solicitud ha sido marcada como pendiente y deberá ser revisada nuevamente."
            : "Póliza actualizada exitosamente",
          type: "success",
        });

        setTimeout(() => {
          navigate("/polizas");
        }, 3000);
      } else {
        setMessage({ text: data.error || "Error al actualizar la póliza", type: "error" });
      }
    } catch (error) {
      console.error("Error updating policy:", error);
      setMessage({ text: "Error al actualizar la póliza", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const getPolicyTypeLabel = (tipo) => {
    const types = {
      seguro_viajero: "Seguro de Viajero",
      seguro_auto: "Seguro de Auto",
      seguro_hogar: "Seguro de Hogar",
      seguro_vida: "Seguro de Vida",
      seguro_gmm: "Gastos Médicos Mayores",
    };
    return types[tipo] || tipo;
  };

  const getStatusBadge = (estado) => {
    const statusConfig = {
      pendiente: {
        icon: Calendar,
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        label: "Pendiente",
      },
      aprobada: {
        icon: CheckCircle,
        color: "bg-green-100 text-green-800 border-green-200",
        label: "Aprobada",
      },
      rechazada: {
        icon: X,
        color: "bg-red-100 text-red-800 border-red-200",
        label: "Rechazada",
      },
      en_revision: {
        icon: AlertCircle,
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "En Revisión",
      },
    };

    const config = statusConfig[estado] || statusConfig.pendiente;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#5fc500]" />
      </div>
    );
  }

  if (!policy) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-16 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/polizas")}
                className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg shadow-md">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-[#218e01] to-[#5fc500] bg-clip-text text-transparent">
                    Editar Póliza
                  </h1>
                  <p className="text-xs text-slate-600">{getPolicyTypeLabel(policy.tipoPoliza)}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(policy.estado)}
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                title="Ver historial"
              >
                <History className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 border border-slate-200">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Advertencia Importante</h3>
                <p className="text-sm text-slate-700 mb-4">
                  Esta póliza está actualmente <strong>aprobada</strong>. Si realizas modificaciones,
                  la solicitud será marcada como <strong>pendiente</strong> y deberá ser revisada y
                  aprobada nuevamente por un administrador.
                </p>
                <p className="text-sm text-slate-600 mb-4">
                  ¿Deseas continuar con la modificación?
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowWarning(false)}
                className="flex-1 px-4 py-2 border-2 border-slate-300 rounded-xl font-semibold text-slate-700 hover:bg-slate-50 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowWarning(false);
                  handleSubmit({ preventDefault: () => {} });
                }}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-xl font-semibold text-white hover:shadow-lg transition-all"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Sidebar */}
      {showHistory && (
        <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm flex items-center justify-end">
          <div className="bg-white h-full w-full max-w-md shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Historial de Cambios</h2>
              <button
                onClick={() => setShowHistory(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {history.length > 0 ? (
                history.map((entry, index) => {
                  const date = entry.timestamp?.toDate
                    ? entry.timestamp.toDate()
                    : entry.timestamp
                      ? new Date(entry.timestamp.seconds * 1000)
                      : new Date();

                  return (
                    <div key={index} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-900">
                          {entry.changedByEmail}
                        </span>
                        <span className="text-xs text-slate-500">
                          {date.toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div className="mb-2">
                        <span className="text-xs font-medium text-slate-600">Estado: </span>
                        <span className="text-xs text-slate-700">{entry.oldStatus}</span>
                        <span className="mx-2 text-slate-400">→</span>
                        <span className="text-xs text-slate-700 font-semibold">{entry.newStatus}</span>
                      </div>
                      {entry.changes && entry.changes.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {entry.changes.map((change, idx) => (
                            <div key={idx} className="text-xs text-slate-600">
                              <span className="font-medium">{change.field}:</span>{" "}
                              <span className="text-slate-500">{String(change.oldValue)}</span>
                              <span className="mx-1 text-slate-400">→</span>
                              <span className="text-slate-700 font-semibold">
                                {String(change.newValue)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {entry.reason && (
                        <div className="mt-2 text-xs text-slate-600">
                          <span className="font-medium">Razón: </span>
                          <span>{entry.reason}</span>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-slate-500 py-8">No hay historial disponible</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Message Alert */}
        {message.text && (
          <div
            className={`mb-8 p-4 rounded-xl border-2 flex items-center gap-3 ${
              message.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            {message.type === "success" ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="font-medium">{message.text}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-12">
          {/* Información Personal */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Información Personal</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nombre Completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nombreCompleto"
                  value={formData.nombreCompleto}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Fecha de Nacimiento <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="date"
                    name="fechaNacimiento"
                    value={formData.fechaNacimiento}
                    onChange={handleInputChange}
                    max={new Date().toISOString().split("T")[0]}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Información del Viaje */}
          {policy.tipoPoliza === "seguro_viajero" && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
                <div className="p-2 bg-gradient-to-r from-[#5fc500] to-[#11653a] rounded-lg">
                  <Plane className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Información del Viaje</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Fecha de Salida <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="date"
                      name="fechaSalida"
                      value={formData.fechaSalida}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Fecha de Regreso <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="date"
                      name="fechaRegreso"
                      value={formData.fechaRegreso}
                      onChange={handleInputChange}
                      min={formData.fechaSalida || new Date().toISOString().split("T")[0]}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Lugares que Visitará <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <MapPin className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                  <textarea
                    name="lugaresVisita"
                    value={formData.lugaresVisita}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="tieneActividadExtrema"
                    checked={formData.tieneActividadExtrema}
                    onChange={handleInputChange}
                    className="w-5 h-5 text-[#5fc500] border-2 border-slate-300 rounded focus:ring-2 focus:ring-[#5fc500]"
                  />
                  <span className="text-sm font-semibold text-slate-700">
                    Realizaré actividades extremas durante el viaje
                  </span>
                </label>

                {formData.tieneActividadExtrema && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Describe las actividades extremas
                    </label>
                    <div className="relative">
                      <Activity className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                      <textarea
                        name="actividadExtrema"
                        value={formData.actividadExtrema}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Beneficiario y Contacto de Emergencia */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#096365] to-[#5ba49e] rounded-lg">
                <UserPlus className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">
                Beneficiario y Contacto de Emergencia
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Beneficiario <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <UserPlus className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="beneficiario"
                    value={formData.beneficiario}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Contacto de Emergencia <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    name="contactoEmergencia"
                    value={formData.contactoEmergencia}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Celular de Emergencia <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="tel"
                  name="celularEmergencia"
                  value={formData.celularEmergencia}
                  onChange={handleInputChange}
                  className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                  required
                />
              </div>
            </div>
          </div>

          {/* Facturación */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#5ba49e] to-[#218e01] rounded-lg">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Facturación</h2>
            </div>

            <div className="mb-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="requiereFactura"
                  checked={formData.requiereFactura}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-[#5fc500] border-2 border-slate-300 rounded focus:ring-2 focus:ring-[#5fc500]"
                />
                <span className="text-sm font-semibold text-slate-700">Requiero factura</span>
              </label>

              {formData.requiereFactura && (
                <div className="mt-4">
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    CSF (Clave de Seguimiento Fiscal) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      type="text"
                      name="csf"
                      value={formData.csf}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white"
                      required={formData.requiereFactura}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notas Adicionales */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Notas Adicionales</h2>
            </div>

            <textarea
              name="notas"
              value={formData.notas}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
              placeholder="Información adicional que consideres relevante..."
            />
          </div>

          {/* Razón del cambio (opcional) */}
          <div className="space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b-2 border-slate-200">
              <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Razón del Cambio</h2>
            </div>

            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all font-medium text-sm bg-white resize-none"
              placeholder="Explica brevemente por qué estás modificando esta solicitud..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => navigate("/polizas")}
              className="flex-1 px-6 py-4 border-2 border-slate-300 rounded-xl font-bold text-slate-700 hover:bg-slate-50 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-xl font-bold text-white shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPolicy;

